import type { StudioDesignSystemPackReleaseBinding } from "@/features/design-system/lib/design-system-pack-release-binding";
import {
  admitDesignSystemGenerationContract,
  DESIGN_SYSTEM_GENERATION_AXES,
} from "@cogo/design-data/generation-design-system";
import { fingerprintAppGenerationSpec } from "@/services/block-api-service";
import {
  APPLICATION_AUTHORITY_POINTER_LIMITS,
  compareApplicationAuthorityStrings,
  encodeApplicationPointerSegment,
  parseUniqueApplicationAuthorityPointers,
  promptAppPlanFromProvidedAppSpec,
  readApplicationAuthorityPointer,
  type StudioProvidedApplicationAuthority,
} from "./application-authority";

export interface StudioExactGenerationAuthority {
  application: Extract<
    StudioProvidedApplicationAuthority,
    { mode: "locked-exact-spec" }
  >;
  designSystemPackRelease: StudioDesignSystemPackReleaseBinding;
  sourceEvidenceRefs: string[];
  rightsEvidenceRefs: string[];
  requiredAssetEvidenceRefs: string[];
}

export const STUDIO_EXACT_GENERATION_AUTHORITY_UNAVAILABLE_CODE =
  "studio-exact-generation-authority-production-receipt-resolver-unavailable";

/**
 * Production exact composition remains fail-closed until a server-owned
 * immutable receipt resolver is available. Caller-supplied reference strings
 * must never be promoted into production authority by the structural
 * validator below.
 */
export function resolveProductionStudioExactGenerationAuthority(
  _input?: StudioExactGenerationAuthority,
): StudioExactGenerationAuthority {
  throw new Error(STUDIO_EXACT_GENERATION_AUTHORITY_UNAVAILABLE_CODE);
}

function normalizeEvidenceRefs(
  refs: readonly string[],
  label: string,
  allRefs: Set<string>,
): string[] {
  if (refs.length === 0) {
    throw new Error(`Exact generation authority requires ${label} evidence.`);
  }
  const normalized = refs.map((ref) => ref.trim());
  if (normalized.some((ref) => !ref)) {
    throw new Error(
      `Exact generation authority requires nonempty ${label} evidence references.`,
    );
  }
  for (const ref of normalized) {
    if (allRefs.has(ref)) {
      throw new Error(
        `Exact generation authority contains duplicate evidence reference ${ref}.`,
      );
    }
    allRefs.add(ref);
  }
  return normalized.sort(compareApplicationAuthorityStrings);
}

function requireReleaseIdentity(value: unknown, label: string): string {
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`Exact generation authority requires release ${label}.`);
  }
  return value.trim();
}

function normalizeAndValidateRelease(
  value: StudioDesignSystemPackReleaseBinding,
): StudioDesignSystemPackReleaseBinding {
  const binding = structuredClone(value);
  if (
    binding?.schemaVersion !==
      "cogo.studio-design-system-pack-release-binding.v2" ||
    !binding.lineage ||
    binding.lineage.schemaVersion !==
      "cogo.studio-design-system-pack-release-lineage.v2" ||
    binding.lineage.source !== "design-system-pack-records" ||
    binding.mode !== "light" ||
    binding.lineage.appliedMode !== "light"
  ) {
    throw new Error(
      "Exact generation authority requires complete release binding lineage.",
    );
  }
  binding.skippedTokenIds.sort(compareApplicationAuthorityStrings);

  const identityPairs = [
    ["releaseId", binding.releaseId, binding.lineage.releaseId],
    ["packageId", binding.packageId, binding.lineage.packageId],
    ["packageVersion", binding.packageVersion, binding.lineage.packageVersion],
    [
      "releaseContentHash",
      binding.releaseContentHash,
      binding.lineage.releaseContentHash,
    ],
    ["packageHash", binding.packageHash, binding.lineage.packageHash],
    [
      "environmentClass",
      binding.environmentClass,
      binding.lineage.environmentClass,
    ],
  ] as const;

  for (const [field, bindingValue, lineageValue] of identityPairs) {
    const normalizedBinding = requireReleaseIdentity(bindingValue, field);
    const normalizedLineage = requireReleaseIdentity(
      lineageValue,
      `lineage ${field}`,
    );
    if (normalizedBinding !== normalizedLineage) {
      throw new Error(
        `Exact generation authority release lineage conflicts on ${field}.`,
      );
    }
    (binding as unknown as Record<string, unknown>)[field] = normalizedBinding;
    (binding.lineage as unknown as Record<string, unknown>)[field] =
      normalizedLineage;
  }

  const generationDesignSystem = admitDesignSystemGenerationContract(
    binding.generationDesignSystem,
  );
  const lineageGenerationDesignSystem = admitDesignSystemGenerationContract(
    binding.lineage.generationDesignSystem,
  );
  if (
    DESIGN_SYSTEM_GENERATION_AXES.some(
      (axis) =>
        generationDesignSystem[axis] !== lineageGenerationDesignSystem[axis],
    )
  ) {
    throw new Error(
      "Exact generation authority release lineage conflicts on generationDesignSystem.",
    );
  }
  binding.generationDesignSystem = generationDesignSystem;
  binding.lineage.generationDesignSystem = lineageGenerationDesignSystem;

  return binding;
}

export function resolveStudioExactGenerationAuthority(
  input: StudioExactGenerationAuthority,
): StudioExactGenerationAuthority {
  if (input.application.openSlots.length > 0) {
    throw new Error("Exact application authority cannot contain an open slot.");
  }

  const lockedPaths = parseUniqueApplicationAuthorityPointers(
    input.application.lockedPaths,
    "locked",
    APPLICATION_AUTHORITY_POINTER_LIMITS.maxLockedPaths,
  ).sort((left, right) =>
    compareApplicationAuthorityStrings(left.canonical, right.canonical),
  );
  for (const pointer of lockedPaths) {
    readApplicationAuthorityPointer(
      input.application.appSpec,
      pointer,
      "Exact application locked path",
    );
  }
  const requiredTopLevelPaths = Object.keys(input.application.appSpec).map(
    (key) => `/${encodeApplicationPointerSegment(key)}`,
  );
  const lockedPathSet = new Set(lockedPaths.map((path) => path.canonical));
  if (requiredTopLevelPaths.some((path) => !lockedPathSet.has(path))) {
    throw new Error(
      "Exact application locked paths must cover the whole supplied AppSpec.",
    );
  }

  const appSpecRef = input.application.appSpecRef;
  if (!appSpecRef) {
    throw new Error(
      "Exact generation authority requires an application spec reference.",
    );
  }
  const expectedAppSpecRef = fingerprintAppGenerationSpec(
    input.application.appSpec,
  );
  if (appSpecRef !== expectedAppSpecRef) {
    throw new Error(
      "Exact application spec reference conflicts with its fingerprint.",
    );
  }

  const plan = promptAppPlanFromProvidedAppSpec(input.application.appSpec);
  const allEvidenceRefs = new Set<string>();
  const applicationEvidenceRefs = normalizeEvidenceRefs(
    input.application.evidenceRefs,
    "application",
    allEvidenceRefs,
  );
  const sourceEvidenceRefs = normalizeEvidenceRefs(
    input.sourceEvidenceRefs,
    "source",
    allEvidenceRefs,
  );
  const rightsEvidenceRefs = normalizeEvidenceRefs(
    input.rightsEvidenceRefs,
    "rights",
    allEvidenceRefs,
  );
  const requiredAssetEvidenceRefs = normalizeEvidenceRefs(
    input.requiredAssetEvidenceRefs,
    "required-asset",
    allEvidenceRefs,
  );

  return {
    application: {
      mode: "locked-exact-spec",
      appSpec: plan.appSpec,
      appSpecRef: plan.appSpecRef,
      evidenceRefs: applicationEvidenceRefs,
      lockedPaths: lockedPaths.map((path) => path.canonical),
      openSlots: [],
    },
    designSystemPackRelease: normalizeAndValidateRelease(
      input.designSystemPackRelease,
    ),
    sourceEvidenceRefs,
    rightsEvidenceRefs,
    requiredAssetEvidenceRefs,
  };
}
